/* eslint-disable @next/next/no-html-link-for-pages */

import Image from "next/image";
import index_hero from "../public/images/hero.png";
import { type Metadata } from "next";

export const metadata: Metadata = {
  title: "Home | Graceful Journey Learning Center",
  description: "Welcome to Graceful Journey Learning Center—offering a faith-based, nurturing environment for children aged birth through 5 years, with over 38 years of experience in early childhood education.",
  keywords: [
    "Graceful Journey Learning Center",
    "Christian daycare",
    "faith-based childcare",
    "child development center",
    "early childhood education",
    "Christian preschool",
    "experienced childcare providers",
  ],
  openGraph: {
    title: "Home | Graceful Journey Learning Center",
    description: "Welcome to Graceful Journey Learning Center—offering a faith-based, nurturing environment for children aged birth through 5 years, with over 38 years of experience in early childhood education.",
    images: [
      {
        url: "/images/hero.png",
        width: 1200,
        height: 630,
        alt: "Graceful Journey - A warm, faith-based learning environment for children",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    title: "Home | Graceful Journey Learning Center",
    description: "Welcome to Graceful Journey Learning Center—offering a faith-based, nurturing environment for children aged birth through 5 years, with over 38 years of experience in early childhood education.",
  },
};

// In all of the testimonials the handle is considered unique, so it's used as the key.
const featuredTestimonial = {
  body: "I've entrusted my children to Graceful Journey Learning Center for years, and I'm truly impressed. With 38 years of experience, their warm, faith-based environment excels in academics while teaching values. Knowledgeable, compassionate educators ensure each child thrives, making a lasting impact. Grateful for their wonderful care and education!",
  author: {
    name: "Samuel Bailey",
    handle: "samuelbailey",
  },
};

const testimonials = [
  [
    [
      {
        body: "Very caring staff. Safe and secure environment.",
        author: {
          name: "John Whitehouse",
          handle: "johnwhitehouse",
        },
      },
      {
        body: "Very nice clean & caring place. If you need childcare they can take care of you.",
        author: {
          name: "John Carr",
          handle: "johncarr",
        },
      },
      {
        body: "Graceful Journey Learning Center stands out for its warm, faith-based environment. The dedicated educators go the extra mile to help children thrive.",
        author: {
          name: "Peter P",
          handle: "peterp",
        },
      },
    ],
    [
      {
        body: "My kids thrive at Graceful Journey Learning Center. Their seasoned staff and faith-based approach create an ideal learning atmosphere.",
        author: {
          name: "Lindsay Walton",
          handle: "lindsaywalton",
        },
      },
      {
        body: "The owners are amazing people!",
        author: {
          name: "Josh J",
          handle: "joshj",
        },
      },
    ],
  ],
  [
    [
      {
        body: "Graceful Journey Learning Center is a true gem for children. Their commitment to a nurturing, faith-based environment, along with dedicated educators, has a lasting impact on young minds.",
        author: {
          name: "Tim A",
          handle: "tima",
        },
      },
      {
        body: "At Graceful Journey Learning Center, my children have found a second home. The blend of academics and faith-based values, coupled with caring educators, ensures their growth.",
        author: {
          name: "Tommy Allen",
          handle: "tommyallen",
        },
      },
    ],
    [
      {
        body: "Graceful Journey Learning Center is a haven for young minds. Their unwavering dedication to a nurturing, faith-based atmosphere and attentive educators have made a significant difference in my children's lives.",
        author: {
          name: "Leonard L",
          handle: "leonardl",
        },
      },
      {
        body: "My kids have blossomed at Graceful Journey Learning Center. With a faith-based foundation and caring educators, this center provides a holistic learning experience.",
        author: {
          name: "James J",
          handle: "Jamesj",
        },
      },
      {
        body: "Graceful Journey Learning Center's commitment to a faith-based environment and the tireless efforts of their educators have been instrumental in shaping my children's growth.",
        author: {
          name: "Marissa Lang",
          handle: "marissalang",
        },
      },
    ],
  ],
];

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

export default function Home() {
  return (
    <>
      <header>
        <title>Graceful Journey Learning Center</title>
      </header>

      {/* Hero */}
      <div className="relative isolate bg-white">
        <div
          className="absolute inset-x-0 top-1/2 -z-10 -translate-y-1/2 transform-gpu overflow-hidden opacity-30 blur-3xl"
          aria-hidden="true"
        >
          <div
            className="ml-[max(50%,38rem)] aspect-1313/771 w-[82.0625rem] bg-linear-to-tr from-[#5aff78] to-[#1dc7ff]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <div
          className="absolute inset-x-0 top-0 -z-10 flex transform-gpu overflow-hidden pt-32 opacity-25 blur-3xl sm:pt-40 xl:justify-end"
          aria-hidden="true"
        >
          <div
            className="ml-[-22rem] aspect-1313/771 w-[82.0625rem] flex-none origin-top-right rotate-[30deg] bg-linear-to-tr from-[#f7ff65] to-[#ff3131] xl:ml-0 xl:mr-[calc(50%-12rem)]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <div className="mx-auto max-w-7xl px-6 py-32 sm:py-40 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8">
            <h1 className="max-w-2xl text-5xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:col-span-2 xl:col-auto">
              Graceful Journey Learning Center
            </h1>
            <div className="mt-6 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
              <p className="text-xl leading-8 text-gray-600">
                Graceful Journey is a full time Christian Childcare Center. We
                teach and care for children birth through 5 years. The
                owner/director has 38 years of childcare experience. All staff
                has a complete background check done, including and FBI
                fingerprint, are certified in infant, child, and adult CPR. They
                also have completed 24 hours of pre-service training and must
                keep up an additional 24 hours of training annually. Come check
                us out, we would love to meet you and your precious children.
              </p>
              <div className="mt-10 flex items-center gap-x-6">
                <a
                  href="/contact"
                  className="rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                >
                  Contact us
                </a>
                <a
                  href="/about"
                  className="text-md font-semibold leading-6 text-gray-900"
                >
                  Learn more <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
            <Image
              src={index_hero}
              alt=""
              className="mt-10 aspect-6/5 w-full max-w-lg rounded-2xl object-cover sm:mt-16 lg:mt-0 lg:max-w-none xl:row-span-2 xl:row-end-2 xl:mt-36"
            />
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="relative isolate bg-white pt-24 sm:pt-32">
        <div
          className="absolute inset-x-0 top-1/2 -z-10 -translate-y-1/2 transform-gpu overflow-hidden opacity-30 blur-3xl"
          aria-hidden="true"
        >
          <div
            className="ml-[max(50%,38rem)] aspect-1313/771 w-[82.0625rem] bg-linear-to-tr from-[#ff80b5] to-[#34c3f3]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <div
          className="absolute inset-x-0 top-0 -z-10 flex transform-gpu overflow-hidden pt-32 opacity-25 blur-3xl sm:pt-40 xl:justify-end"
          aria-hidden="true"
        >
          <div
            className="ml-[-22rem] aspect-1313/771 w-[82.0625rem] flex-none origin-top-right rotate-[30deg] bg-linear-to-tr from-[#ff80b5] to-[#26c9ff] xl:ml-0 xl:mr-[calc(50%-12rem)]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="text-lg font-semibold leading-8 tracking-tight text-sky-600">
              Testimonials
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              We have worked with thousands of amazing people
            </p>
          </div>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 grid-rows-1 gap-8 text-sm leading-6 text-gray-900 sm:mt-20 sm:grid-cols-2 xl:mx-0 xl:max-w-none xl:grid-flow-col xl:grid-cols-4">
            <figure className="col-span-2 hidden sm:block sm:rounded-2xl sm:bg-white sm:shadow-lg sm:ring-1 sm:ring-gray-900/5 xl:col-start-2 xl:row-end-1">
              <blockquote className="p-12 text-xl font-semibold leading-8 tracking-tight text-gray-900">
                <p>{`“${featuredTestimonial.body}”`}</p>
              </blockquote>
              <figcaption className="flex items-center gap-x-4 border-t border-gray-900/10 px-6 py-4">
                <div className="flex-auto">
                  <div className="font-semibold">
                    {featuredTestimonial.author.name}
                  </div>
                </div>
              </figcaption>
            </figure>
            {testimonials.map((columnGroup, columnGroupIdx) => (
              <div
                key={columnGroupIdx}
                className="space-y-8 xl:contents xl:space-y-0"
              >
                {columnGroup.map((column, columnIdx) => (
                  <div
                    key={columnIdx}
                    className={classNames(
                      (columnGroupIdx === 0 && columnIdx === 0) ||
                        (columnGroupIdx === testimonials.length - 1 &&
                          columnIdx === columnGroup.length - 1)
                        ? "xl:row-span-2"
                        : "xl:row-start-1",
                      "space-y-8",
                    )}
                  >
                    {column.map((testimonial) => (
                      <figure
                        key={testimonial.author.handle}
                        className="rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-900/5"
                      >
                        <blockquote className="text-gray-900">
                          <p>{`“${testimonial.body}”`}</p>
                        </blockquote>
                        <figcaption className="mt-6 flex items-center gap-x-4">
                          <div>
                            <div className="font-semibold">
                              {testimonial.author.name}
                            </div>
                          </div>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Learn more */}
      <div className="relative isolate bg-white">
        <div
          className="absolute inset-x-0 top-1/2 -z-10 -translate-y-1/2 transform-gpu overflow-hidden opacity-30 blur-3xl"
          aria-hidden="true"
        >
          <div
            className="ml-[max(50%,38rem)] aspect-1313/771 w-[28.0625rem] bg-linear-to-tr from-[#9187ff] to-[#2623ff]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <div className="px-6 py-16 sm:px-6 sm:py-28 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Want to learn more?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-600">
              Check out our frequently asked questions page to learn more about
              our services. If you have other questions or concerns, feel free
              to contact our team. We’re here to help.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a
                href="/faq"
                className="rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
              >
                Learn more
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
